import React, { useEffect, useState } from 'react';
import { Box, Button, Container, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography, Alert, Chip, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Collapse, Tooltip, Badge, CircularProgress, LinearProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import TestIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import DeleteIcon from '@mui/icons-material/Delete';
import MessageIcon from '@mui/icons-material/Message';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SyncIcon from '@mui/icons-material/Sync';
import { useNavigate } from 'react-router-dom';
import { tgAccountsAPI } from '../../Api';
import PlumSvg from '../component/PlumSvg';

const TGAccountsList = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [alert, setAlert] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reloginLoadingId, setReloginLoadingId] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState({});
  const [syncingOrders, setSyncingOrders] = useState(false);
  
  // Auth dialog states
  const [authStep, setAuthStep] = useState('idle');
  const [authAccountId, setAuthAccountId] = useState(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [authMessage, setAuthMessage] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  
  const navigate = useNavigate();

  const showAlert = (type, message) => {
    setAlert(<Alert severity={type}>{message}</Alert>);
    setTimeout(() => setAlert(null), 5000);
  };

  const load = async () => {
    try {
      const res = await tgAccountsAPI.getAccounts({ page: page + 1, limit: rowsPerPage, search });
      const data = res.data?.data || {};
      const baseItems = data.items || [];
      // Fetch live status for each account to reflect actual TDLib state
      const itemsWithLiveStatus = await Promise.all(
        baseItems.map(async (acc) => {
          try {
            const st = await tgAccountsAPI.getStatus(acc._id);
            const live = st?.data?.data?.status;
            return { ...acc, status: live || acc.status };
          } catch (_) {
            return acc;
          }
        })
      );
      setItems(itemsWithLiveStatus);
      setTotal(data.total || 0);
    } catch (e) {
      setItems([]);
      setTotal(0);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

  const handleTestMessage = async (accountId) => {
    setTestingId(accountId);
    try {
      const res = await tgAccountsAPI.testMessage(accountId, '🎯 Тестовое сообщение из веб-панели!');
      if (res.data?.success) {
        showAlert('success', res.data.data.message || 'Тестовое сообщение отправлено!');
      } else {
        showAlert('error', 'Ошибка отправки сообщения');
      }
    } catch (e) {
      showAlert('error', e?.response?.data?.message || e?.message || 'Ошибка отправки тестового сообщения');
    } finally {
      setTestingId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
      case 'ready':
        return 'success';
      case 'wait_code':
      case 'wait_password':
      case 'wait_phone':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleDeleteClick = (account) => {
    setAccountToDelete(account);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!accountToDelete) return;
    
    setDeleting(true);
    try {
      const res = await tgAccountsAPI.deleteAccount(accountToDelete._id);
      if (res.data?.success) {
        showAlert('success', res.data.message || 'Аккаунт успешно удален');
        load(); // Reload list
      } else {
        showAlert('error', 'Ошибка удаления аккаунта');
      }
    } catch (e) {
      showAlert('error', e?.response?.data?.message || e?.message || 'Ошибка удаления аккаунта');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setAccountToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAccountToDelete(null);
  };

  const toggleOrdersExpansion = (accountId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  };

  const formatOrderStatus = (status) => {
    const statusMap = {
      'WAIT_PAY': { label: 'Ожидает оплаты', color: 'warning' },
      'WAIT_CONFIRM': { label: 'Ожидает подтверждения', color: 'info' },
      'WAIT_PAY_LINK': { label: 'Ожидает ссылку', color: 'secondary' },
      'pending': { label: 'В обработке', color: 'default' },
      'PAID': { label: 'Оплачен', color: 'success' },
      'FAILED': { label: 'Отклонен', color: 'error' }
    };
    return statusMap[status] || { label: status, color: 'default' };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSyncOrders = async () => {
    setSyncingOrders(true);
    try {
      const res = await tgAccountsAPI.syncOrders();
      if (res.data?.success) {
        showAlert('success', res.data.message || 'Счетчики синхронизированы');
        load(); // Reload list to show updated counters
      } else {
        showAlert('error', 'Ошибка синхронизации счетчиков');
      }
    } catch (e) {
      showAlert('error', e?.response?.data?.message || e?.message || 'Ошибка синхронизации счетчиков');
    } finally {
      setSyncingOrders(false);
    }
  };

  const startAuthFlow = async (accountId) => {
    try {
      setAuthStep('connecting');
      setAuthMessage('Подключение к Telegram...');
      setAuthLoading(true);
      setAuthAccountId(accountId);
      
      // Poll status every 1 second
      const pollStatus = async () => {
        try {
          const statusRes = await tgAccountsAPI.getStatus(accountId);
          const status = statusRes?.data?.data?.status;
          console.log('Current status:', status);
          
          if (!status) {
            setTimeout(pollStatus, 1000);
            return;
          }
          
          switch (status) {
            case 'wait_params':
              setAuthMessage('Установка параметров TDLib...');
              break;
            case 'wait_encryption':
              setAuthMessage('Настройка шифрования...');
              break;
            case 'wait_phone':
              setAuthMessage('Отправка номера телефона...');
              break;
            case 'wait_code':
              setAuthStep('wait_code');
              setAuthMessage('Введите код из SMS');
              setAuthLoading(false);
              return; // Stop polling
            case 'wait_password':
              setAuthStep('wait_password');
              setAuthMessage('Введите пароль 2FA');
              setAuthLoading(false);
              return; // Stop polling
            case 'active':
            case 'ready':
              setAuthStep('success');
              setAuthMessage('Авторизация успешна!');
              setAuthLoading(false);
              setTimeout(() => {
                resetAuthFlow();
                load(); // Reload the list
              }, 1500);
              return; // Stop polling
            case 'error':
              setAuthStep('error');
              setAuthMessage('Ошибка авторизации');
              setAuthLoading(false);
              return; // Stop polling
            default:
              setAuthMessage(`Статус: ${status || 'неизвестно'}`);
          }
          
          setTimeout(pollStatus, 1000);
        } catch (e) {
          console.error('Status poll error:', e);
          setTimeout(pollStatus, 2000);
        }
      };
      
      // Start polling
      setTimeout(pollStatus, 1000);
      
    } catch (error) {
      setAuthStep('error');
      setAuthMessage('Ошибка запуска авторизации');
      setAuthLoading(false);
    }
  };

  const resetAuthFlow = () => {
    setAuthStep('idle');
    setAuthAccountId(null);
    setCode('');
    setPassword('');
    setAuthMessage('');
    setAuthLoading(false);
    setResendLoading(false);
  };

  const handleCodeSubmit = async () => {
    if (!code.trim() || !authAccountId) return;
    setAuthLoading(true);
    try {
      await tgAccountsAPI.submitCode(authAccountId, code);
      // Continue polling in startAuthFlow
    } catch (e) {
      showAlert('error', e?.response?.data?.message || 'Неверный код');
      setAuthLoading(false);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!password.trim() || !authAccountId) return;
    setAuthLoading(true);
    try {
      await tgAccountsAPI.submitPassword(authAccountId, password);
      // Continue polling in startAuthFlow
    } catch (e) {
      showAlert('error', e?.response?.data?.message || 'Неверный пароль');
      setAuthLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!authAccountId) return;
    setResendLoading(true);
    try {
      await tgAccountsAPI.resendCode(authAccountId);
      showAlert('success', 'Код отправлен повторно');
    } catch (e) {
      showAlert('error', e?.response?.data?.message || 'Ошибка отправки кода');
    } finally {
      setResendLoading(false);
    }
  };

  const handleRelogin = async (accountId) => {
    setReloginLoadingId(accountId);
    try {
      const res = await tgAccountsAPI.relogin(accountId);
      if (res.data?.success) {
        showAlert('success', 'Перезапуск авторизации выполнен. Проверьте SMS...');
        // Start polling status to show auth dialog
        startAuthFlow(accountId);
      } else {
        showAlert('error', 'Не удалось перезапустить авторизацию');
      }
    } catch (e) {
      showAlert('error', e?.response?.data?.message || e?.message || 'Ошибка перезахода');
    } finally {
      setReloginLoadingId(null);
    }
  };

  return (
    <Container maxWidth="sx">
      <Box sx={{ mt: 8, p: 4, boxShadow: 3, borderRadius: 2, bgcolor: '#fefcfb' }}>
        <Typography variant="h5" align="center" gutterBottom>
          <PlumSvg /> TG Аккаунты
        </Typography>

        {alert}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <TextField size="small" label="Поиск (phone, clientid, apikey, type)" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} sx={{ minWidth: 320 }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Синхронизировать счетчики ордеров">
              <Button 
                variant="outlined" 
                startIcon={<SyncIcon />} 
                onClick={handleSyncOrders}
                disabled={syncingOrders}
                size="small"
              >
                {syncingOrders ? 'Синхронизация...' : 'Синхронизировать'}
              </Button>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/tg-accounts/add')} sx={{ backgroundColor: '#683d62' }}>Добавить</Button>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Phone</TableCell>
                <TableCell>Client ID</TableCell>
                <TableCell>API Key</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Ордера / Слоты</TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <React.Fragment key={row._id}>
                  <TableRow hover>
                    <TableCell>{row.phone}</TableCell>
                    <TableCell>{row.clientid}</TableCell>
                    <TableCell>
                      {row.apikey ? `${row.apikey.substring(0, 8)}...` : '-'}
                    </TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>
                      <Chip 
                        label={row.status || 'unknown'} 
                        color={getStatusColor(row.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={`Активных ордеров: ${row.activeOrdersCount || 0}\nЗанято слотов: ${row.orders || 0}`}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                            <Badge badgeContent={row.activeOrdersCount || 0} color="primary">
                              <ShoppingCartIcon color={row.activeOrdersCount > 0 ? 'primary' : 'disabled'} />
                            </Badge>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontSize: '0.7rem',
                                color: row.orders > 0 ? 'primary.main' : 'text.secondary',
                                fontWeight: row.orders > 0 ? 'bold' : 'normal'
                              }}
                            >
                              Слотов: {row.orders || 0}
                            </Typography>
                          </Box>
                        </Tooltip>
                        {row.activeOrdersCount > 0 && (
                          <Tooltip title={expandedOrders[row._id] ? 'Скрыть ордера' : 'Показать ордера'}>
                            <IconButton 
                              size="small" 
                              onClick={() => toggleOrdersExpansion(row._id)}
                            >
                              {expandedOrders[row._id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton size="small" onClick={() => navigate(`/tg-accounts/edit/${row._id}`)}>
                        <EditIcon />
                      </IconButton>
                      {(row.status === 'active' || row.status === 'ready') && (
                        <>
                          <IconButton 
                            size="small" 
                            onClick={() => navigate(`/tg-accounts/${row._id}/chats`)}
                            color="info"
                            title="Просмотр чатов"
                          >
                            <ChatIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => navigate(`/tg-accounts/${row._id}/send-message`)}
                            color="success"
                            title="Отправить сообщение пользователю"
                          >
                            <MessageIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleTestMessage(row._id)}
                            disabled={testingId === row._id}
                            color="primary"
                            title="Отправить тестовое сообщение в Избранное"
                          >
                            <TestIcon />
                          </IconButton>
                        </>
                      )}
                      {(row.status === 'need_login' || (!row.status || (row.status !== 'active' && row.status !== 'ready'))) && (
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={() => handleRelogin(row._id)}
                          disabled={reloginLoadingId === row._id}
                        >
                          {reloginLoadingId === row._id ? 'Перезаход...' : 'Перезайти'}
                        </Button>
                      )}
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteClick(row)}
                        color="error"
                        title="Удалить аккаунт"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
                
                {/* Expandable Orders Row */}
                {row.activeOrdersCount > 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0 }}>
                      <Collapse in={expandedOrders[row._id]} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                          <Typography variant="h6" gutterBottom component="div" sx={{ fontSize: '0.9rem' }}>
                            Активные ордера ({row.activeOrdersCount})
                          </Typography>
                          <Table size="small" aria-label="active orders">
                            <TableHead>
                              <TableRow>
                                <TableCell>ID Ордера</TableCell>
                                <TableCell>Статус</TableCell>
                                <TableCell>Сумма</TableCell>
                                <TableCell>Платформа</TableCell>
                                <TableCell>Способ оплаты</TableCell>
                                <TableCell>Создан</TableCell>
                                <TableCell>Payment ID</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {row.activeOrders?.map((order) => (
                                <TableRow key={order._id}>
                                  <TableCell component="th" scope="row">
                                    <Typography variant="body2" fontWeight="bold">
                                      {order.order_id}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={formatOrderStatus(order.status).label} 
                                      color={formatOrderStatus(order.status).color}
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {order.amount ? `${order.amount} ₽` : '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {order.platform || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {order.payment_method || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                      {formatDate(order.created_at)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                      {order.payment_id || '-'}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">Нет данных</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите удалить аккаунт <strong>{accountToDelete?.phone}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'warning.main' }}>
            ⚠️ Это действие нельзя отменить. Будут удалены:
          </DialogContentText>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>Запись аккаунта из базы данных</li>
            <li>Активная TDLib сессия (если запущена)</li>
            <li>Все связанные данные</li>
          </Box>
          <DialogContentText sx={{ mt: 2, fontSize: '0.9rem', color: 'text.secondary' }}>
            Файлы сессии на диске останутся и могут быть удалены вручную.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Отмена
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Удаление...' : 'Удалить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auth Dialog */}
      <Dialog 
        open={authStep !== 'idle'} 
        onClose={authStep === 'error' ? resetAuthFlow : undefined}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {authStep === 'connecting' && 'Авторизация Telegram'}
          {authStep === 'wait_code' && 'Введите SMS код'}
          {authStep === 'wait_password' && 'Введите пароль 2FA'}
          {authStep === 'success' && 'Успешно!'}
          {authStep === 'error' && 'Ошибка'}
        </DialogTitle>
        
        <DialogContent>
          {authLoading && <LinearProgress sx={{ mb: 2 }} />}
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {authMessage}
          </Typography>

          {authStep === 'wait_code' && (
            <TextField
              autoFocus
              label="Код из SMS"
              placeholder="12345"
              fullWidth
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={authLoading}
              onKeyPress={(e) => e.key === 'Enter' && handleCodeSubmit()}
              sx={{ mt: 1 }}
            />
          )}

          {authStep === 'wait_password' && (
            <TextField
              autoFocus
              label="Пароль 2FA"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={authLoading}
              onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>

        <DialogActions>
          {authStep === 'error' && (
            <Button onClick={resetAuthFlow}>Закрыть</Button>
          )}
          
          {authStep === 'wait_code' && (
            <>
              <Button onClick={resetAuthFlow} disabled={authLoading}>Отмена</Button>
              <Button 
                onClick={handleResendCode}
                disabled={authLoading || resendLoading}
              >
                {resendLoading ? <CircularProgress size={20} /> : 'Отправить код повторно'}
              </Button>
              <Button 
                variant="contained" 
                onClick={handleCodeSubmit}
                disabled={authLoading || !code.trim()}
              >
                {authLoading ? <CircularProgress size={20} /> : 'Отправить'}
              </Button>
            </>
          )}
          
          {authStep === 'wait_password' && (
            <>
              <Button onClick={resetAuthFlow} disabled={authLoading}>Отмена</Button>
              <Button 
                variant="contained" 
                onClick={handlePasswordSubmit}
                disabled={authLoading || !password.trim()}
              >
                {authLoading ? <CircularProgress size={20} /> : 'Отправить'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TGAccountsList;
